/**
 * Layout Component
 * 
 * Main layout shell for the dashboard with sync status indicator.
 */

import { useState, useEffect } from 'react';
import { Cloud, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { getSyncMode, syncTasks } from '../api/client';
import type { SyncMode } from '../types';
import { UserMenu } from './UserMenu';
import { LogoMark } from './LogoMark';
import { TrialBanner } from './TrialBanner';
import { SampleTaskBanner } from './SampleTaskBanner';

interface LayoutProps {
  children: React.ReactNode;
  breadcrumb?: string;
  onSettingsClick?: () => void;
  onInsightsClick?: () => void;
}

export function Layout({ children, breadcrumb, onSettingsClick, onInsightsClick }: LayoutProps) {
  const [syncMode, setSyncMode] = useState<SyncMode | null>(null);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'success' | 'error'>('idle');
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  // Load sync mode on mount
  useEffect(() => {
    loadSyncMode();
  }, []);

  // Auto-sync in Todoist mode every 5 minutes
  useEffect(() => {
    if (syncMode?.mode === 'todoist') {
      const interval = setInterval(() => {
        handleAutoSync();
      }, 5 * 60 * 1000); // 5 minutes

      return () => clearInterval(interval);
    }
  }, [syncMode]);

  const loadSyncMode = async () => {
    try {
      const mode = await getSyncMode();
      setSyncMode(mode);
    } catch (err) {
      console.error('Failed to load sync mode:', err);
    }
  };

  const handleAutoSync = async () => {
    if (syncStatus === 'syncing') return; // Don't sync if already syncing

    setSyncStatus('syncing');
    try {
      await syncTasks();
      setSyncStatus('success');
      setLastSyncTime(new Date());
      // Reset to idle after 2 seconds
      setTimeout(() => setSyncStatus('idle'), 2000);
    } catch (err) {
      setSyncStatus('error');
      // Reset to idle after 5 seconds
      setTimeout(() => setSyncStatus('idle'), 5000);
    }
  };

  const handleManualSync = async () => {
    if (syncMode?.mode !== 'todoist' || syncStatus === 'syncing') return;
    await handleAutoSync();
  };

  const syncTitle =
    syncMode?.mode === 'standalone'
      ? 'Local mode — no sync'
      : lastSyncTime
        ? `Last synced: ${lastSyncTime.toLocaleTimeString()}`
        : 'Click to sync';

  return (
    <div
      className="flex flex-col bg-surface-base-light dark:bg-surface-base-dark"
      style={{ height: '100dvh' }}
    >
      <TrialBanner />
      <SampleTaskBanner />
      {/* Header */}
      <header className="shrink-0 h-11 bg-surface-raised-light dark:bg-surface-raised-dark border-b border-border-subtle-light dark:border-border-subtle-dark hidden md:flex items-center justify-between px-4 z-50">

        {/* Left: logo + breadcrumb path */}
        <div className="flex items-center gap-3 min-w-0">
          <LogoMark className="h-5 w-auto shrink-0" />

          {breadcrumb && (
            <>
              {/* Vertical rule */}
              <div className="hidden sm:block h-[18px] w-px bg-border-subtle-light dark:bg-border-subtle-dark" />

              <span className="hidden sm:flex items-center gap-1.5 font-mono text-xs md:text-[11px] tracking-widest">
                <span className="hidden md:inline font-normal uppercase text-text-tertiary-light dark:text-text-tertiary-dark">danny</span>
                <span className="hidden md:inline opacity-25 select-none">/</span>
                <span className="font-semibold uppercase text-text-primary-light dark:text-text-primary-dark">
                  {breadcrumb}
                </span>
              </span>
            </>
          )}
        </div>

        {/* Right: sync (only shown for Todoist users) + avatar */}
        <div className="flex items-center gap-1">
          {syncMode?.mode === 'todoist' && (
            <button
              onClick={handleManualSync}
              disabled={syncStatus === 'syncing'}
              title={syncTitle}
              className={`relative p-2.5 md:p-1.5 rounded transition-colors text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark ${
                syncStatus === 'syncing' ? 'cursor-wait' : ''
              }`}
            >
              {syncStatus === 'syncing' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {syncStatus === 'success' && <CheckCircle2 className="w-3.5 h-3.5" />}
              {syncStatus === 'error'   && <AlertCircle  className="w-3.5 h-3.5 text-red-500" />}
              {syncStatus === 'idle'    && <Cloud         className="w-3.5 h-3.5" />}
              {/* Status dot */}
              {(syncStatus === 'idle' || syncStatus === 'success') && (
                <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-green-500" />
              )}
              {syncStatus === 'error' && (
                <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-red-500" />
              )}
            </button>
          )}

          <UserMenu
            onSettingsClick={onSettingsClick}
            onInsightsClick={onInsightsClick}
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 min-h-0 overflow-hidden">
        {children}
      </main>
    </div>
  );
}

